export { getBootHTML } from "./BootSequenceTemplate.ts";

export interface BootSequenceActions {
  delay(ms: number, fn: () => void): void;
  interval(ms: number, fn: () => void): void;
  isDestroyed(): boolean;
  getPhase(): number;
  /** BL-143 — fired once, when the FIRST red `threat:true` line renders (the
   *  "ECHELON INTRUSION" cue) → the boot threat sting. Optional (headless/tests). */
  onFirstThreat?(): void;
}

/** Run Phase 1: POST — threat lines with glitch effects */
export function runPostPhase(
  overlay: HTMLDivElement,
  actions: BootSequenceActions,
  onComplete: () => void,
): void {
  const postEl = overlay.querySelector("#boot-post") as HTMLElement;
  if (!postEl) return;
  const grid = overlay.querySelector(".boot-grid") as HTMLElement;
  if (grid) grid.style.opacity = "0";

  const postBg = document.createElement("div");
  postBg.className = "post-bg-pulse";
  postEl.appendChild(postBg);
  const postScan = document.createElement("div");
  postScan.className = "post-scanlines";
  postEl.appendChild(postScan);
  const postNoise = document.createElement("div");
  postNoise.className = "post-noise";
  postEl.appendChild(postNoise);
  const postVignette = document.createElement("div");
  postVignette.className = "post-vignette";
  postEl.appendChild(postVignette);
  const postContent = document.createElement("div");
  postContent.className = "post-content";
  postEl.appendChild(postContent);

  const lines: { text: string; threat: boolean; echelon?: boolean }[] = [
    { text: "> AXIOM-77 MAINFRAME BOOT SEQUENCE", threat: false },
    { text: "> CHECKING MEMORY BANKS...        [OK]", threat: false },
    { text: "> LOADING CORE ROUTINES...        [OK]", threat: false },
    { text: "> INITIALIZING DEFENSE NODES...   [OK]", threat: false },
    { text: "> SCANNING I/O CHANNELS...        [OK]", threat: false },
    { text: "> AI CORE STATUS:                 [ONLINE]", threat: false },
    { text: "", threat: false },
    // S126: HER line carries the corruption magenta (the theme's first seed).
    {
      text: "> !! THREAT DETECTED: ECHELON INTRUSION !!",
      threat: true,
      echelon: true,
    },
    { text: "> !! MULTIPLE ATTACK VECTORS ACTIVE !!", threat: true },
    { text: "> !! DEFENSE PROTOCOL REQUIRED !!", threat: true },
    { text: "", threat: false },
    {
      text: "> SECURITY CLEARANCE: \u2588\u2588\u2588\u2588 OMEGA \u2588\u2588\u2588\u2588",
      threat: false,
    },
    { text: "> LOADING TACTICAL INTERFACE...", threat: false },
  ];

  let idx = 0;
  const typeLine = (): void => {
    if (actions.isDestroyed() || idx >= lines.length) {
      actions.delay(300, onComplete);
      return;
    }
    const line = lines[idx]!;
    const lineEl = document.createElement("div");
    lineEl.className = `post-line${line.threat ? " post-line-threat" : ""}${
      line.echelon ? " post-line-echelon" : ""
    }`;
    lineEl.textContent = line.text;
    postContent.appendChild(lineEl);

    if (line.threat) {
      const threatIdx = lines.slice(0, idx + 1).filter((l) => l.threat).length;
      const variants = [
        {
          signal: "signal-loss-heavy",
          tear: "post-tear-heavy",
          dur: 650,
          tearDur: 450,
        },
        {
          signal: "signal-loss-med",
          tear: "post-tear-med",
          dur: 400,
          tearDur: 350,
        },
        {
          signal: "signal-loss-light",
          tear: "post-tear-light",
          dur: 250,
          tearDur: 250,
        },
      ];
      const v = variants[Math.min(threatIdx - 1, 2)]!;
      const glitchDelay = 30 + Math.random() * 150;
      setTimeout(() => {
        const signalLoss = document.createElement("div");
        signalLoss.className = v.signal;
        overlay.appendChild(signalLoss);
        setTimeout(() => signalLoss.remove(), v.dur);
        postEl.classList.add(v.tear);
        setTimeout(() => postEl.classList.remove(v.tear), v.tearDur);
        if (threatIdx <= 2) {
          const flash = document.createElement("div");
          flash.className = "boot-threat-flash";
          overlay.appendChild(flash);
          setTimeout(() => flash.remove(), 600);
        }
      }, glitchDelay);
      if (threatIdx === 1) {
        // BL-143 — the alarm sting fires with the first red threat line.
        actions.onFirstThreat?.();
        const preFlick = document.createElement("div");
        preFlick.className = "signal-pre-flicker";
        overlay.appendChild(preFlick);
        setTimeout(() => preFlick.remove(), 200);
      }
      if (threatIdx === 3) {
        setTimeout(
          () => {
            const aftershock = document.createElement("div");
            aftershock.className = "signal-loss-light";
            overlay.appendChild(aftershock);
            setTimeout(() => aftershock.remove(), 250);
          },
          600 + Math.random() * 300,
        );
      }
    }

    idx++;
    let nextDelay: number;
    if (line.threat) {
      const tIdx = lines.slice(0, idx).filter((l) => l.threat).length;
      nextDelay =
        tIdx === 1
          ? 700 + Math.random() * 200
          : tIdx === 2
            ? 400 + Math.random() * 150
            : 300 + Math.random() * 100;
    } else {
      nextDelay = 200 + Math.random() * 80;
    }
    actions.delay(nextDelay, typeLine);
  };
  typeLine();
}

/** Start live metric animations (uptime, flicker, clock, glitch) */
export function startLiveAnimations(
  overlay: HTMLDivElement,
  actions: BootSequenceActions,
): void {
  let sec = 3693431;
  const uptimeEl = overlay.querySelector("#boot-uptime");
  actions.interval(1000, () => {
    sec++;
    const h = Math.floor(sec / 3600);
    const m = Math.floor((sec % 3600) / 60);
    const s = sec % 60;
    if (uptimeEl)
      uptimeEl.textContent = `${String(h).padStart(4, "0")}:${String(m).padStart(2, "0")}:${String(s).padStart(2, "0")}`;
  });

  actions.interval(900, () => {
    const inf = overlay.querySelector("#boot-infer");
    const acc = overlay.querySelector("#boot-acc");
    const lat = overlay.querySelector("#boot-latency");
    const cpu = overlay.querySelector("#boot-cpu");
    const ram = overlay.querySelector("#boot-ram");
    const gpu = overlay.querySelector("#boot-gpu");
    const disk = overlay.querySelector("#boot-disk");
    const vt = overlay.querySelector("#boot-vectors");
    const fw = overlay.querySelector("#boot-firewall");
    const inferV = 8.2 + Math.random() * 0.4;
    const accV = 99.2 + Math.random() * 0.7;
    const cpuV = 40 + Math.random() * 5;
    const ramV = 68 + Math.floor(Math.random() * 12);
    const vecV = 44 + Math.floor(Math.random() * 8);
    const fwV = 85 + Math.floor(Math.random() * 10);
    if (inf) inf.textContent = `${inferV.toFixed(2)} GFLOPS`;
    if (acc) acc.textContent = `${accV.toFixed(1)}%`;
    if (lat) lat.textContent = `${(0.2 + Math.random() * 0.3).toFixed(1)}ms`;
    if (cpu) cpu.textContent = `${cpuV.toFixed(1)}\u00B0C`;
    if (ram) ram.textContent = `${ramV}%`;
    if (gpu) gpu.textContent = `${(1.7 + Math.random() * 0.3).toFixed(1)} GHz`;
    if (disk) disk.textContent = `${200 + Math.floor(Math.random() * 80)} MB/s`;
    if (vt) vt.textContent = `${vecV}`;
    if (fw) fw.textContent = `${fwV}%`;
    // Update data-honest bars to match their values
    const accBar = overlay.querySelector("#boot-acc-bar") as HTMLElement;
    const cpuBar = overlay.querySelector("#boot-cpu-bar") as HTMLElement;
    const ramBar = overlay.querySelector("#boot-ram-bar") as HTMLElement;
    const vecBar = overlay.querySelector("#boot-vectors-bar") as HTMLElement;
    const fwBar = overlay.querySelector("#boot-firewall-bar") as HTMLElement;
    if (accBar) accBar.style.width = `${accV}%`;
    if (cpuBar) cpuBar.style.width = `${cpuV}%`;
    if (ramBar) ramBar.style.width = `${ramV}%`;
    if (vecBar) vecBar.style.width = `${Math.min(vecV * 2, 100)}%`;
    if (fwBar) fwBar.style.width = `${fwV}%`;
  });

  const timeEl = overlay.querySelector("#boot-time");
  actions.interval(1000, () => {
    const now = new Date();
    const ts = `2084-${String(now.getMonth() + 1).padStart(2, "0")}-${String(now.getDate()).padStart(2, "0")} ${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}:${String(now.getSeconds()).padStart(2, "0")}`;
    if (timeEl) timeEl.textContent = ts;
  });

  actions.interval(2500, () => {
    const texts = overlay.querySelectorAll<HTMLElement>(
      ".boot-kv .v, .boot-brow .v",
    );
    const el = texts[Math.floor(Math.random() * texts.length)];
    if (el) {
      el.classList.add("glitch-flash");
      setTimeout(() => el.classList.remove("glitch-flash"), 150);
    }
  });

  scheduleAmbientGlitch(overlay, actions);
  scheduleTitleGlitch(overlay, actions);
}

function scheduleTitleGlitch(
  overlay: HTMLDivElement,
  actions: BootSequenceActions,
): void {
  const nextIn = 4000 + Math.random() * 4000;
  actions.delay(nextIn, () => {
    if (actions.isDestroyed() || actions.getPhase() !== 3) return;
    const titleBlock = overlay.querySelector(
      ".boot-title-block",
    ) as HTMLElement;
    if (titleBlock) {
      titleBlock.classList.add("boot-title-glitch");
      setTimeout(() => titleBlock.classList.remove("boot-title-glitch"), 200);
    }
    scheduleTitleGlitch(overlay, actions);
  });
}

function scheduleAmbientGlitch(
  overlay: HTMLDivElement,
  actions: BootSequenceActions,
): void {
  const nextIn = 8000 + Math.random() * 17000;
  actions.delay(nextIn, () => {
    if (actions.isDestroyed() || actions.getPhase() !== 3) return;
    const roll = Math.random();
    let cls: string;
    let dur: number;
    if (roll < 0.6) {
      cls = "signal-loss-light";
      dur = 250;
    } else if (roll < 0.9) {
      cls = "signal-loss-med";
      dur = 400;
    } else {
      cls = "signal-loss-heavy";
      dur = 650;
    }

    const glitch = document.createElement("div");
    glitch.className = cls;
    overlay.appendChild(glitch);
    setTimeout(() => glitch.remove(), dur);

    if (roll >= 0.6) {
      const grid = overlay.querySelector(".boot-grid") as HTMLElement;
      if (grid) {
        const tearCls = roll >= 0.9 ? "post-tear-med" : "post-tear-light";
        grid.classList.add(tearCls);
        setTimeout(() => grid.classList.remove(tearCls), 350);
      }
    }
    scheduleAmbientGlitch(overlay, actions);
  });
}
